<template>
  <div class="view" v-if="canEdit" v-cloak>
    <EditCreateTitleBox :label="title" />
    <div v-if="isLoading" class="loader-container">
      <Loader :loading="isLoading" size="sm"></Loader>
    </div>
    <div class="invoice-management-add-edit-container" v-else>
      <!-- Left Box -->
      <StepsPanelWrapper
        ref="stepsWrapper"
        :steps="steps"
        :current-step.sync="currentStep"
        :step-props-fn="getStepProps"
        @step-input="onStepInput"
        @validation-error="handleValidationError"
      />

      <!-- Right Box -->
      <InvoiceManagementRightPanel :value="formData" :saving="saving" @save="save" @back="gotoList" />
    </div>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import settings from "./partials/settings.vue";
import InvoiceReceptionItems from "./partials/InvoiceReceptionItems.vue";
import additionalData from "./partials/additionalData.vue";
import InvoiceManagementRightPanel from "./partials/InvoiceManagementRightPanel.vue";
import { canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "@/pages/errors/Forbidden";
import EditCreateTitleBox from "@/components/Common/EditCreateTitleBox.vue";
import StepsPanelWrapper from "@/components/Common/StepsPanelWrapper.vue";
import { successMessage, errorMessage, handlePromiseException } from "@/helpers/common";
import Loader from "@/components/Loader";
import moment from "moment";

export default {
  name: "InvoiceManagementAddOrEdit",
  data() {
    return {
      title: this.$t("stock_management.invoice_management.edit"),
      isLoading: false,
      formData: {
        uuid: null,
        department_uuid: null,
        department_name: "",
        invoice_number: "",
        partner_uuid: null,
        partner_name: "",
        date: null,
        due_date: null,
        payment_date: null,
        items: [],
        vat_code: null,
        //additional data
        notes: null,
      },
      steps: [
        {
          number: 1,
          component: settings,
          label: this.$t("common.base_data"),
          ref: "InvoiceManagementSettings",
        },
        {
          number: 2,
          component: InvoiceReceptionItems,
          label: this.$t("stock_management.invoice_management.items"),
          ref: "InvoiceManagementItems",
        },
        {
          number: 3,
          component: additionalData,
          label: this.$t("common.additional_data"),
          ref: "InvoiceManagementAdditionalData",
        },
      ],
      currentStep: 1, // Set the current active step
      saving: false,
    };
  },
  computed: {
    ...mapGetters({
      quantityDecimalPlaces: "settings/getQuantityDecimalPlaces",
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.SM_INVOICE_MANAGEMENT,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
  },
  components: {
    settings,
    InvoiceReceptionItems,
    additionalData,
    InvoiceManagementRightPanel,
    Forbidden,
    EditCreateTitleBox,
    StepsPanelWrapper,
    Loader,
  },
  methods: {
    close() {},
    ...mapActions({
      createSmInvoiceManagement: "smInvoiceManagement/createSmInvoice",
      updateSmInvoiceManagement: "smInvoiceManagement/updateSmInvoice",
      getSmInvoiceManagementByUuid: "smInvoiceManagement/getSmInvoiceByUuid",
    }),
    handleAddSave(dataToSave) {
      this.createSmInvoiceManagement(dataToSave)
        .then((response) => {
          successMessage(this, response.message);
          this.$router.push({ name: "invoice-management" });
        })
        .catch((e) => {
          handlePromiseException(this, e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    handleEditSave(dataToSave) {
      this.updateSmInvoiceManagement(dataToSave)
        .then((response) => {
          successMessage(this, response.message);
          this.$router.push({ name: "invoice-management" });
        })
        .catch((e) => {
          handlePromiseException(this, e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async fetchSmInvoiceManagementData(uuid) {
      try {
        this.getSmInvoiceManagementByUuid({
          uuid: uuid,
        }).then((response) => {
          if (response && response.data) {
            response.data.date = moment(response.data.date).format(this.getDefaultDateFormat); // Format the date to the default format
            response.data.due_date = moment(response.data.due_date).format(this.getDefaultDateFormat); // Format the date to the default format
            if (response.data.payment_date && response.data.payment_date !== "eet") {
              response.data.payment_date = moment(response.data.payment_date).format(this.getDefaultDateFormat); // Format the date to the default format
            }

            this.formData = { ...this.formData, ...response.data };

            for (const step of this.steps) {
              const stepComponent = this.$refs?.[step.ref];
              if (stepComponent && typeof stepComponent.updateData === "function") {
                if (step.number === 1) {
                  stepComponent.updateData(response.data);
                } else {
                  setTimeout(() => {
                    stepComponent.updateData(response.data);
                  }, step.number * 10);
                }
              }
            }
          }
        });
      } finally {
        this.isLoading = false;
      }
    },
    getStepProps() {
      return {
        formData: this.formData,
      };
    },
    onStepInput(step, payload) {
      if (!step) return;
      this.formData = { ...this.formData, ...(payload || {}) };
    },
    handleValidationError(error) {
      errorMessage(this, error.message || this.$t("fill_out_error"));
    },
    save() {
      this.validateStepsAndSave();
    },
    async validateStepsAndSave() {
      const wrapper = this.$refs?.stepsWrapper;
      if (wrapper && typeof wrapper.validateSteps === "function") {
        const errors = await Promise.resolve(wrapper.validateSteps());
        if (errors && errors.length) {
          errors.forEach((msg) => errorMessage(this, msg));
          return;
        }
      }

      this.saving = true;
      const dataToSave = {
        ...this.formData,
      };

      if (dataToSave.uuid) {
        // in edit mode do not change field report status
        this.handleEditSave(dataToSave);
      } else {
        this.handleAddSave(dataToSave);
      }
    },
    gotoList() {
      this.$router.push({ name: "invoice-management" });
    },
    roundToDecimals(value, decimals) {
      if (typeof value !== "number") value = Number(value) || 0;
      return Number(value.toFixed(decimals));
    },
  },
  mounted() {
    if (this.$route.params.uuid && this.$route.params.uuid !== "add") {
      this.formData.uuid = this.$route.params.uuid;
      this.title = this.$t("stock_management.invoice_management.edit");
      this.isLoading = true;
      setTimeout(() => {
        this.fetchSmInvoiceManagementData(this.$route.params.uuid);
      }, 500);
    } else {
      this.formData.uuid = null;
      this.title = this.$t("stock_management.invoice_management.create_new");
    }
  },
};
</script>

<style scoped>
.view {
  height: calc(100% - 70px);
}

.invoice-management-add-edit-container {
  display: flex;
  height: 100%;
  width: 100%;
}
</style>

<style>
.invoice-management-add-edit-container .dx-selectbox {
  margin-top: 8px !important;
}

.invoice-management-add-edit-container .dx-textbox {
  margin-top: 8px !important;
}
</style>
